import React, { useState, useEffect, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import "./styles/CustomCheckout.css";
import {
  GREATER_BANK,
  NEWCASTLE_PERMANENT_BANK,
  TRIAL_PERIOD_DAYS,
} from "../../redux/enums";
import { rejectCard } from "../../redux/actions/cardActions";
import { useDispatch, useSelector } from "react-redux";
import { updateBank } from "../../redux/actions/bankActions";

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY as string
);

// Helper function to track checkout events
const trackCheckoutEvent = (eventName: string, eventDetails: any = {}) => {
  // Create and dispatch custom event
  const trackingEvent = new CustomEvent("grocerize_checkout_event", {
    bubbles: true,
    cancelable: true,
    detail: {
      event: eventName,
      timestamp: new Date().toISOString(),
      ...eventDetails,
    },
  });
  document.dispatchEvent(trackingEvent);
  console.log(`Checkout tracking: ${eventName}`, eventDetails);
};

// The checkout form component
const CheckoutForm: React.FC<{
  customerId: string;
  promoCode?: string;
  userEmail: string;
  hideHeaderText?: boolean;
  onPromoCodeClear?: () => void;
}> = ({
  customerId,
  promoCode: initialPromoCode,
  userEmail,
  hideHeaderText = true,
  onPromoCodeClear,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { getAccessTokenSilently, user } = useAuth0(); // Get user object from Auth0
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>("card"); // 'card' or 'apple_pay'
  const [isApplePayComplete, setIsApplePayComplete] = useState(false);

  const dispatch = useDispatch();
  const bankType = useSelector((state: ReduxState) => state.bank.name);

  // State to control bank verification modal visibility
  const [showBankVerificationModal, setShowBankVerificationModal] =
    useState(false);
  const [bankForVerification, setBankForVerification] = useState({
    name: "Greater Bank",
    cardImageUrl: "/images/greater_bank_card_img.png",
    promoMonths: 6,
  });

  // Initialize email from Auth0 user data
  const [email, setEmail] = useState(userEmail || "");

  // State for user-entered promo code
  const [promoCode, setPromoCode] = useState(initialPromoCode || "");

  // Make sure email field is updated when userEmail prop changes
  useEffect(() => {
    if (userEmail && userEmail !== email) {
      setEmail(userEmail);
    }
  }, [userEmail, email]);

  // Initialize promo code from props
  useEffect(() => {
    if (initialPromoCode && initialPromoCode !== promoCode) {
      setPromoCode(initialPromoCode);
    }
  }, [initialPromoCode, promoCode]);

  // Get current date
  const today = new Date();

  // We always display $0.00 for the trial period

  // Function to verify promo code - wrapped in useCallback to avoid infinite renders
  const verifyPromoCode = useCallback(
    async (code) => {
      if (!code) return;

      try {
        const token = await getAccessTokenSilently();
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/payments/verify-promo-code`,
          { promo_code: code },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.valid) {
          dispatch(updateBank(response.data.bankType || null)); // Update bank type in Redux
          return {
            isValid: true,
            message: response.data.message || "Promo code applied successfully",
            trialDays: response.data.benefits?.trialDays || TRIAL_PERIOD_DAYS,
          };
        } else {
          dispatch(updateBank(null));
          return {
            isValid: false,
            message: response.data.message || "Invalid promo code",
          };
        }
      } catch (error) {
        dispatch(updateBank(null));
        console.error("Error verifying promo code:", error);
        return {
          isValid: false,
          message: "Error verifying promo code",
        };
      }
    },
    [getAccessTokenSilently]
  );

  // State to track promo code verification details
  const [promoCodeData, setPromoCodeData] = useState({
    isValid: false,
    trialDays: TRIAL_PERIOD_DAYS,
    description: "",
    isVerifying: false,
    attempted: false, // Track if verification has been attempted
    bankType: null as string | null, // Track if promo code is for a specific bank
  });

  // Calculate trial period based on verified promo code
  const trialDays = promoCodeData.isValid
    ? promoCodeData.trialDays
    : TRIAL_PERIOD_DAYS;
  const trialEndDate = new Date(today);
  trialEndDate.setDate(today.getDate() + trialDays);
  console.log("promo code trial days:", promoCodeData.trialDays);

  // Clear promo code data when code is removed
  useEffect(() => {
    if (!promoCode) {
      setPromoCodeData((prev) => ({
        ...prev,
        isValid: false,
        trialDays: TRIAL_PERIOD_DAYS,
        description: "",
        attempted: false, // Reset attempt status when code is cleared
        bankType: null, // Reset bank type when code is cleared
      }));
    }
  }, [promoCode]);

  // Verify promo code when it changes and update state
  useEffect(() => {
    const verifyCode = async () => {
      if (!promoCode) {
        setPromoCodeData((prev) => ({
          ...prev,
          isValid: false,
          trialDays: TRIAL_PERIOD_DAYS,
          description: "",
          attempted: false, // Reset attempt status when code is cleared
          bankType: null, // Reset bank type when code is cleared
        }));
        return;
      }

      setPromoCodeData((prev) => ({
        ...prev,
        isVerifying: true,
      }));

      try {
        const result = await verifyPromoCode(promoCode);

        if (result && result.isValid) {
          // Detect bank type from promo code but only set for display in payment flow
          // Don't set bankType here to avoid duplicate UI elements

          setPromoCodeData({
            isValid: true,
            trialDays: result.trialDays || TRIAL_PERIOD_DAYS, // Use trial days from API response
            description: result.message || "",
            isVerifying: false,
            attempted: true,
            bankType: null, // Only set bankType during payment processing, not during promo validation
          });
          console.log(
            "Updated promo code data with trial days:",
            result.trialDays
          );
        } else {
          setPromoCodeData({
            isValid: false,
            trialDays: TRIAL_PERIOD_DAYS, // Default to TRIAL_PERIOD_DAYS days for invalid codes
            description: result ? result.message : "Invalid promo code",
            isVerifying: false,
            attempted: true,
            bankType: null,
          });
        }
      } catch (error) {
        console.error("Error verifying promo code:", error);
        setPromoCodeData({
          isValid: false,
          trialDays: TRIAL_PERIOD_DAYS,
          description: "Error verifying promo code",
          isVerifying: false,
          attempted: true,
          bankType: null,
        });
      }
    };

    verifyCode();
  }, [promoCode, verifyPromoCode]);

  // Function to handle successful Apple Pay payment - wrapped in useCallback to prevent dependency cycle
  const handleApplePaySuccess = useCallback(() => {
    console.log("Apple Pay payment successful!");
    setPaymentMethod("apple_pay");
    setIsApplePayComplete(true);
    setErrorMessage("");
    setIsPaymentSuccessful(true);

    // Track successful Apple/Google Pay payment
    trackCheckoutEvent("payment_success", {
      method: "wallet_payment",
      paymentType: "apple_pay", // Use static value instead of state to avoid dependency issues
    });

    // Submit the form after successful Apple Pay with a delay to show success state
    setTimeout(() => {
      window.location.href = "/browse?welcome_premium=true";
    }, 1500);
  }, []); // Empty dependency array since we don't want this function to change

  // Track when checkout form is viewed
  useEffect(() => {
    trackCheckoutEvent("checkout_page_view", { hasPromoCode: !!promoCode });
  }, [promoCode]);

  // Handle scroll indicator visibility
  useEffect(() => {
    const checkButtonVisibility = () => {
      const submitButton = document.getElementById("checkout-submit-button");
      const scrollIndicator = document.getElementById("scroll-indicator");

      if (submitButton && scrollIndicator) {
        const buttonRect = submitButton.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Check if button is below the viewport
        if (buttonRect.top > windowHeight || buttonRect.bottom > windowHeight) {
          scrollIndicator.style.display = "flex";
        } else {
          scrollIndicator.style.display = "none";
        }
      }
    };

    // Check on initial render
    setTimeout(checkButtonVisibility, 500); // Small delay to ensure elements are rendered

    // Check on scroll
    window.addEventListener("scroll", checkButtonVisibility);

    // Check on resize
    window.addEventListener("resize", checkButtonVisibility);

    // Clean up event listeners
    return () => {
      window.removeEventListener("scroll", checkButtonVisibility);
      window.removeEventListener("resize", checkButtonVisibility);
    };
  }, []);

  // Set up payment request for Apple Pay and Google Pay
  useEffect(() => {
    // Prevent multiple initializations of payment request
    let isInitialized = false;

    if (stripe && !isInitialized) {
      isInitialized = true;
      console.log("Initializing payment request object");

      const paymentRequestObject = stripe.paymentRequest({
        country: "AU",
        currency: "aud",
        total: {
          label: "Premium Subscription",
          amount: 0, // Free for trial period
        },
        requestPayerName: true,
        requestPayerEmail: true,
        // The presence of these fields allows compatibility with more wallets
        requestShipping: false,
      });

      // Check if the Payment Request is supported (Apple Pay or Google Pay)
      paymentRequestObject
        .canMakePayment()
        .then((result) => {
          if (result) {
            console.log("Payment Request is available:", result);
            // The result object may contain information about which payment methods are available
            // For example: result.applePay or result.googlePay may be true
            setPaymentRequest(paymentRequestObject);
          } else {
            console.log("No supported payment wallets available");
          }
        })
        .catch((error) => {
          console.error("Error checking payment wallet availability:", error);
        });

      // Setup event handler for when payment method is received
      paymentRequestObject.on("paymentmethod", async (event) => {
        // Track wallet payment attempt
        trackCheckoutEvent("wallet_payment_attempt", { paymentType: "wallet" });
        console.log("Payment Method received!", event);
        try {
          // Get Auth0 ID from user object - this is what the backend expects
          const auth0Id = user?.sub || "";
          const currentEmail = email; // Capture current email value to avoid closure issues
          const currentPromoCode = promoCode; // Capture current promo code to avoid closure issues

          // Get authentication token from Auth0
          const token = await getAccessTokenSilently();

          // Create subscription on your backend
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/payments/create-subscription`,
            {
              customer: auth0Id,
              payment_method_id: event.paymentMethod.id,
              email: currentEmail,
              promo_code: currentPromoCode,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          console.log("Backend response:", response.data);

          // Check if subscription exists and has an ID (using new response format)
          if (response.data.subscriptionId) {
            console.log(
              "Subscription created successfully:",
              response.data.subscriptionId
            );
            // Complete the Apple Pay payment successfully
            await event.complete("success");

            // Track successful payment
            trackCheckoutEvent("payment_success", {
              method: "wallet_payment",
              subscriptionId: response.data.subscriptionId,
              trialDays: response.data.trial_days || TRIAL_PERIOD_DAYS,
              promoApplied: response.data.promo_applied || false,
            });

            handleApplePaySuccess();
          } else {
            // Handle API errors
            let errorMessage =
              response.data.message || "Subscription creation failed";

            // Check for specific error structure with detailed error information
            if (response.data.error) {
              errorMessage = response.data.error.message || errorMessage;

              // Track error details for analytics
              trackCheckoutEvent("payment_error", {
                method: "wallet",
                errorType: response.data.error.code || "subscription_creation",
                errorDetail: response.data.error.detail || "",
                errorMessage: errorMessage,
              });
            } else {
              // Track generic subscription creation error
              trackCheckoutEvent("payment_error", {
                method: "wallet",
                errorType: "subscription_creation",
                errorMessage: errorMessage,
              });
            }

            await event.complete("fail");
            setErrorMessage(errorMessage);
          }
        } catch (error) {
          console.error("Error processing payment:", error);
          await event.complete("fail");

          let errorMessage = "Error processing payment";

          // Check if error is an axios error with response data
          if (error.response && error.response.data) {
            // Handle structured error responses
            if (
              error.response.data.error &&
              error.response.data.error.message
            ) {
              errorMessage = error.response.data.error.message;

              // Track specific API error
              trackCheckoutEvent("payment_error", {
                method: "wallet",
                errorType: error.response.data.error.code || "api_error",
                errorDetail: error.response.data.error.detail || "",
                errorMessage: errorMessage,
              });
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message;

              // Track generic API error with message
              trackCheckoutEvent("payment_error", {
                method: "wallet",
                errorType: "api_error",
                errorMessage: errorMessage,
              });
            }
          } else {
            // Track generic wallet payment error
            trackCheckoutEvent("payment_error", {
              method: "wallet",
              errorType: "unexpected_error",
              errorMessage: error.message || "Unknown error",
            });
          }

          setErrorMessage(errorMessage);
        }
      });

      // Return cleanup function
      return () => {
        console.log("Cleaning up payment request");
        isInitialized = false;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, handleApplePaySuccess]); // Intentionally excluding email, promoCode, and user?.sub to prevent infinite loops

  // Format the date as Month Day, Year
  const formatDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-AU", options);
  };

  // Handle form submission
  const handleSubmit = async (event?: React.FormEvent) => {
    // If event exists (direct form submission), prevent default behavior
    if (event) {
      event.preventDefault();
    }

    // Track payment attempt
    trackCheckoutEvent("payment_attempt", { method: paymentMethod });

    // If Apple Pay was used and payment completed, we can skip card processing
    if (paymentMethod === "apple_pay" && isApplePayComplete) {
      console.log("Apple Pay already processed, redirecting to success page");
      window.location.href = "/browse?welcome_premium=true";
      return;
    }

    if (!stripe || !elements) {
      setErrorMessage("Please wait while we load the payment form.");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setIsProcessing(false);
      setErrorMessage("Could not find card element");
      return;
    }

    try {
      // Create payment method
      const { error: createError, paymentMethod: method } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        });

      if (createError) {
        console.error("Error creating payment method:", createError);
        setErrorMessage(createError.message);
        setIsProcessing(false);

        // Track payment error
        trackCheckoutEvent("payment_error", {
          method: "card",
          errorType: "create_payment_method",
          errorMessage: createError.message,
        });

        return;
      }

      // Get Auth0 ID from user object
      const auth0Id = user?.sub || "";
      console.log("Using Auth0 ID for backend API:", auth0Id);

      // Get authentication token from Auth0
      const token = await getAccessTokenSilently();

      // Create subscription on backend
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/payments/create-subscription`,
        {
          customer: auth0Id,
          payment_method_id: method.id,
          email,
          promo_code: promoCode,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Check for BIN code to determine bank type
      let redirectUrl = "/browse?welcome_premium=true";

      if (response.data.promo_applied && response.data.card_details?.bin) {
        const trialDays = response.data.trial_days || TRIAL_PERIOD_DAYS;

        if (bankType === NEWCASTLE_PERMANENT_BANK) {
          redirectUrl = `/browse?welcome_premium=true&bank_type=${bankType}&trial_days=${trialDays}`;
          console.log("Newcastle Permanent card detected");
        } else if (bankType === GREATER_BANK) {
          redirectUrl = `/browse?welcome_premium=true&bank_type=${bankType}&trial_days=${trialDays}`;
          console.log("Greater Bank card detected");
        }
      }

      // Check if subscription exists and has an ID (using new response format)
      if (response.data.subscriptionId) {
        console.log(
          "Subscription created successfully:",
          response.data.subscriptionId
        );
        // Show success state before redirecting
        setIsPaymentSuccessful(true);
        setIsProcessing(false);

        // Track successful payment
        trackCheckoutEvent("payment_success", {
          method: "card",
          subscriptionId: response.data.subscriptionId,
          trialDays: response.data.trial_days || TRIAL_PERIOD_DAYS,
          promoApplied: response.data.promo_applied || false,
          bin: response.data.card_details?.bin || "unknown",
        });

        // Redirect after a short delay to show success state
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1500);
      } else {
        // Handle API errors
        let errorMessage =
          response.data.message || "Error creating subscription";

        // Check for specific error structure with detailed error information
        if (response.data.error) {
          errorMessage = response.data.error.message || errorMessage;
          const errorType =
            response.data.error.type || response.data.error.code || "";
          const errorMessageLower = errorMessage.toLowerCase();

          console.log("Payment error details:", {
            message: errorMessage,
            type: errorType,
            detail: response.data.error.detail || "",
          });

          // Check if this is a promo bin check failed error or any card eligibility error
          if (
            errorType === "promo_bin_check_failed" ||
            errorMessageLower.includes("not eligible for this promotion") ||
            errorMessageLower.includes("card from the designated bank")
          ) {
            dispatch(rejectCard(true));
            console.log(
              "Detected card eligibility error for promo, showing bank verification modal"
            );

            if (bankType === NEWCASTLE_PERMANENT_BANK) {
              setBankForVerification({
                name: "Newcastle Permanent",
                cardImageUrl: "/images/newcastle_bank_card_img.png",
                promoMonths: 6,
              });
            } else if (bankType === GREATER_BANK) {
              setBankForVerification({
                name: "Greater Bank",
                cardImageUrl: "/images/greater_bank_card_img.png",
                promoMonths: 6,
              });
            } else {
              // Default to Greater Bank if we can't determine the bank
              setBankForVerification({
                name: "Greater Bank",
                cardImageUrl: "/images/greater_bank_card_img.png",
                promoMonths: 6,
              });
            }

            setShowBankVerificationModal(true);
            setIsProcessing(false);
            return; // Don't show regular error message
          }

          // Track error details for analytics
          trackCheckoutEvent("payment_error", {
            method: "card",
            errorType: errorType || "subscription_creation",
            errorDetail: response.data.error.detail || "",
            errorMessage: errorMessage,
          });
        } else {
          // Track generic subscription creation error
          trackCheckoutEvent("payment_error", {
            method: "card",
            errorType: "subscription_creation",
            errorMessage: errorMessage,
          });
        }

        setErrorMessage(errorMessage);
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error("Error processing payment:", error);

      let errorMessage = "An unexpected error occurred";

      // Check if error is an axios error with response data
      if (error.response && error.response.data) {
        // Check for specific error structure with detailed error information
        if (error.response.data.error && error.response.data.error.message) {
          errorMessage = error.response.data.error.message;
          const errorType =
            error.response.data.error.type ||
            error.response.data.error.code ||
            "";
          const errorMessageLower = errorMessage.toLowerCase();

          // Check for promo eligibility errors
          if (
            errorType === "promo_bin_check_failed" ||
            errorMessageLower.includes("not eligible for this promotion") ||
            errorMessageLower.includes("card from the designated bank")
          ) {
            dispatch(rejectCard(true));
            console.log(
              "Detected card eligibility error in API error response"
            );

            // Determine which bank's promo code was used
            const promoCodeLower = promoCode.toLowerCase();

            if (bankType === NEWCASTLE_PERMANENT_BANK) {
              setBankForVerification({
                name: "Newcastle Permanent",
                cardImageUrl: "/images/newcastle_card_img.png",
                promoMonths: 6,
              });
            } else if (bankType === GREATER_BANK) {
              setBankForVerification({
                name: "Greater Bank",
                cardImageUrl: "/images/greater_bank_card_img.png",
                promoMonths: 6,
              });
            } else {
              // Default to Greater Bank if we can't determine the bank
              setBankForVerification({
                name: "Greater Bank",
                cardImageUrl: "/images/greater_bank_card_img.png",
                promoMonths: 6,
              });
            }

            setShowBankVerificationModal(true);
            setIsProcessing(false);
            return; // Skip showing regular error
          }

          // Track specific API error
          trackCheckoutEvent("payment_error", {
            method: "card",
            errorType: errorType || "api_error",
            errorDetail: error.response.data.error.detail || "",
            errorMessage: errorMessage,
          });
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;

          // Track generic API error with message
          trackCheckoutEvent("payment_error", {
            method: "card",
            errorType: "api_error",
            errorMessage: errorMessage,
          });
        } else {
          // Track unexpected API error
          trackCheckoutEvent("payment_error", {
            method: "card",
            errorType: "unexpected_api_error",
            errorMessage: error.message || "Unknown API error",
            statusCode: error.response.status,
          });
        }
      } else {
        // Track non-API unexpected error
        trackCheckoutEvent("payment_error", {
          method: "card",
          errorType: "unexpected_error",
          errorMessage: error.message || "Unknown error",
        });
      }

      setErrorMessage(errorMessage);
      setIsProcessing(false);
    }
  };

  const hideBankVerificationModal = () => {
    setShowBankVerificationModal(false);
    dispatch(rejectCard(false));
  };

  // Render the Bank Verification Modal outside of all containers
  if (showBankVerificationModal) {
    return (
      <div
        style={{
          backgroundColor: "#9300DA" /* Full magenta background */,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 10000,
          padding: "40px 20px",
          overflow: "auto",
        }}
      >
        {/* Close button in top right */}
        <button
          style={{
            position: "absolute",
            top: 18,
            right: 18,
            background: "none",
            border: "none",
            color: "white",
            cursor: "pointer",
            padding: 0,
            zIndex: 10001,
          }}
          onClick={() => {
            hideBankVerificationModal();
          }}
          aria-label="Close bank verification modal"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        {/* Content container - wraps everything */}
        <div
          style={{
            width: "100%",
            maxWidth: "600px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {/* Premium Logo */}
          {/* <div style={{
            marginBottom: '30px',
            marginTop: '20px'
          }}>
            <img 
              src="https://grocerize-assets.s3.amazonaws.com/frontend/grocerize-premium-white.svg"
              alt="Grocerize Premium"
              style={{ width: '280px', height: 'auto' }}
            />
          </div> */}

          {/* Credit Card Image */}
          <div
            style={{
              marginBottom: "30px",
              backgroundColor: "#ffffff20",
              borderRadius: "16px",
              padding: "15px",
              width: "fit-content",
            }}
          >
            <img
              src={bankForVerification.cardImageUrl}
              alt={`${bankForVerification.name} Card`}
              style={{
                width: "300px",
                maxWidth: "100%",
                height: "auto",
                borderRadius: "10px",
              }}
            />
          </div>

          {/* Verification Text */}
          <div
            style={{
              color: "white",
              marginBottom: "20px",
              maxWidth: "500px",
            }}
          >
            <p
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "10px",
                lineHeight: "1.3",
              }}
            >
              To redeem your {bankForVerification.promoMonths}
              -month FREE subscription to Grocerize Premium, you must use your{" "}
              {bankForVerification.name} Visa card.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="u-flex u-w-all u-flex-column u-mt--24">
          <button
            className="u-mb--8 u-background--white u-color-magenta u-border--none u-pv--16 u-ph--20 u-br--large u-font--16 u-bold u-shadow--light" //magenta is violet, @TODO change this name
            onClick={() => {
              setPromoCode("");
              hideBankVerificationModal();
              if (onPromoCodeClear) {
                onPromoCodeClear();
              }
            }}
          >
            Continue with the card I've entered, to receive 7 days free
          </button>

          <button
            className="u-background--white u-color-magenta u-border--none u-pv--16 u-ph--20 u-br--large u-font--16 u-bold u-shadow--ligh"
            onClick={() => hideBankVerificationModal()}
          >
            Take me back to enter my {bankForVerification.name} card
          </button>
        </div>
      </div>
    );
  }

  return (
    //@TODO we might want to use the existing classnames define in src/styles/utils
    <form onSubmit={handleSubmit} className="premium-checkout-form single-step">
      {/* Main checkout content */}

      {/* Timeline visualization */}
      <div className="single-step-timeline">
        <div className="timeline-point filled"></div>
        <div className="timeline-line"></div>
        <div className="timeline-point empty"></div>
        <div className="timeline-texts">
          <p className="timeline-text">
            Today:{" "}
            {promoCodeData.trialDays >= 28
              ? `${Math.round(promoCodeData.trialDays / 30)} ${
                  Math.round(promoCodeData.trialDays / 30) === 1
                    ? "month"
                    : "months"
                }`
              : `${promoCodeData.trialDays} days`}{" "}
            for A$0.00
          </p>
          <p className="timeline-text second">
            Starting on {formatDate(trialEndDate)}: A$2.95
          </p>
        </div>
      </div>

      {/* Total section */}
      <div className="total-now">
        <span className="total-label">Total Now</span>
        <span className="total-price">A$0.00</span>
      </div>

      {/* Payment methods */}
      <div className="payment-methods">
        {/* Payment Request Button - supports both Apple Pay and Google Pay */}
        {paymentRequest && (
          <div id="payment-request-button">
            <PaymentRequestButtonElement
              options={{
                paymentRequest,
                style: {
                  paymentRequestButton: {
                    type: "buy",
                    theme: "dark",
                    height: "48px",
                  },
                },
              }}
            />
          </div>
        )}
        {/* Payment method: Card */}
        <div
          className="payment-option selected"
          style={{
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            padding: "16px",
            backgroundColor: "#fff",
          }}
        >
          <div
            className="u-flex-wrap"
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                border: "2px solid #9300DA",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginRight: "12px",
              }}
            >
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: "#9300DA",
                }}
              ></div>
            </div>
            <span>Credit or debit card</span>
            <div className="card-brands">
              <img
                src="https://js.stripe.com/v3/fingerprinted/img/visa-365725566f9578a9589553aa9296d178.svg"
                alt="Visa"
                className="card-brand"
              />
              <img
                src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg"
                alt="Mastercard"
                className="card-brand"
              />
              <img
                src="https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg"
                alt="American Express"
                className="card-brand"
              />
              <img
                src="https://js.stripe.com/v3/fingerprinted/img/unionpay-8a10aefc7295216c338ba4e1224627a1.svg"
                alt="UnionPay"
                className="card-brand"
              />
            </div>
          </div>

          {/* Stripe Card Element */}
          <div className="card-element-container">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
                hidePostalCode: true, // Remove ZIP/postal code field
              }}
            />
          </div>

          {/* Promo code status display (field has been moved to signup form) */}
          <div style={{ marginTop: "8px" }}>
            {promoCodeData.isVerifying && (
              <p
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#666",
                  marginTop: "4px",
                  fontStyle: "italic",
                }}
              >
                Verifying...
              </p>
            )}
            {promoCode && !promoCodeData.isVerifying && promoCodeData.isValid && (
              <p
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  marginTop: "4px",
                  color: "#00843d",
                }}
              >
                ✓ {promoCodeData.description}
              </p>
            )}
            {promoCode &&
              !promoCodeData.isVerifying &&
              !promoCodeData.isValid &&
              promoCodeData.attempted && (
                <p
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    marginTop: "4px",
                    color: "#d32f2f",
                  }}
                >
                  {promoCodeData.description || "Invalid promo code"}
                </p>
              )}
          </div>
        </div>
      </div>

      {/* Error message display */}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

      {/* Email display (readonly) */}
      <div className="hidden-email">
        <input type="hidden" id="email" value={email} readOnly required />
      </div>

      {/* Terms section */}
      <div className="terms-section">
        <p className="terms-text">
          By clicking "Start saving", you consent to get immediate access to
          Premium. If you don't cancel before the trial ends on{" "}
          {formatDate(trialEndDate)}, you'll be charged A$2.95/month. Cancel
          anytime.
          <a
            href="/sign-up-terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-link"
          >
            Terms apply
          </a>
        </p>
      </div>

      {/* Submit button */}
      <button
        id="checkout-submit-button"
        className={`btn-submit ${isPaymentSuccessful ? "success" : ""}`}
        type="submit"
        disabled={!stripe || isProcessing || isPaymentSuccessful}
      >
        {isProcessing ? (
          "Processing..."
        ) : isPaymentSuccessful ? (
          <span className="success-text">
            <svg
              className="checkmark"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 52 52"
            >
              <circle
                className="checkmark-circle"
                cx="26"
                cy="26"
                r="25"
                fill="none"
              />
              <path
                className="checkmark-check"
                fill="none"
                d="M14.1 27.2l7.1 7.2 16.7-16.8"
              />
            </svg>
            Payment Complete
          </span>
        ) : (
          "Start saving"
        )}
      </button>

      {/* Scroll indicator that appears when button is off-screen */}
      {/* <div
        id="scroll-indicator"
        className="scroll-indicator"
        onClick={() => {
          const submitButton = document.getElementById(
            "checkout-submit-button"
          );
          if (submitButton) {
            submitButton.scrollIntoView({ behavior: "smooth" });
          }
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M11 4h2v12l5.5-5.5 1.42 1.42L12 19.84l-7.92-7.92L5.5 10.5 11 16V4z" />
        </svg>
      </div> */}
    </form>
  );
};

// Interface for SingleStepCheckout props
interface SingleStepCheckoutProps {
  customerId?: string;
  promoCode?: string;
  hideHeaderText?: boolean;
  onPromoCodeClear?: () => void;
}

// Main component that wraps the checkout form with Stripe Elements
const SingleStepCheckout: React.FC<SingleStepCheckoutProps> = ({
  customerId = "",
  promoCode = "",
  hideHeaderText = true,
  onPromoCodeClear,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error] = useState<string | null>(null); // Error state for displaying errors

  useEffect(() => {
    // Wait for Auth0 to load
    if (isLoading) return;

    // If not authenticated, user will be redirected by ProtectedRoute
    if (!isAuthenticated || !user) return;

    // Once we have user data from Auth0, we're ready to show the checkout
    setIsDataLoading(false);

    // Log authentication status
    console.log("User authenticated for checkout:", user.email);
  }, [isLoading, isAuthenticated, user]);

  // Use provided customerId or get from Auth0 user
  const finalCustomerId = customerId || user?.sub || "";

  // Get final promo code - either from props (for modal) or URL params (for standalone page)
  const finalPromoCode =
    promoCode ||
    (() => {
      const params = new URLSearchParams(window.location.search);
      return params.get("promo_code") || "";
    })();

  if (isLoading || isDataLoading) {
    return (
      <div className="custom-checkout-container">
        <div className="loading-container">
          <p>Loading checkout information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="custom-checkout-container">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-checkout-container">
      <Elements stripe={stripePromise}>
        <CheckoutForm
          customerId={finalCustomerId}
          promoCode={finalPromoCode}
          userEmail={user?.email || ""}
          hideHeaderText={hideHeaderText}
          onPromoCodeClear={onPromoCodeClear}
        />
      </Elements>
    </div>
  );
};

export default SingleStepCheckout;
